import React from 'react';
import { ChevronDown } from 'lucide-react';

interface CalculatorFormProps {
  state: {
    projectDurationMonths: number;
    softwareType: string;
    expectedUsers: number;
    avgUsageHoursPerWeek: number;
    userRegion: string;
    devRegion: string;
    infraRegion: string;
    projectLifespanYears: number;
  };
  softwareTypes: string[];
  regions: { name: string; factor: number; countries?: string[] }[];
  onInputChange: (field: string, value: string | number) => void;
  validationErrors: Record<string, boolean>;
}

const CarbonCalculatorForm: React.FC<CalculatorFormProps> = ({ state, onInputChange, softwareTypes, regions, validationErrors }) => {
  return (
    <div className="bg-white p-6 rounded-xl shadow-lg border border-slate-200 sticky top-4">
      <h3 className="text-xl font-semibold text-slate-900 mb-6">Paramètres du Projet</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="col-span-1 md:col-span-2">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Type de Logiciel
          </label>
          <div className="relative">
            <select
              value={state.softwareType}
              onChange={(e) => onInputChange('softwareType', e.target.value)}
              className={`w-full px-4 py-3 rounded-lg border ${validationErrors.softwareType ? 'border-red-500' : 'border-slate-200'} focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none transition-all appearance-none bg-white pr-10`}
            >
              <option value="">Sélectionnez un type de logiciel</option>
              {softwareTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
              <ChevronDown className="w-5 h-5 text-slate-400" />
            </div>
          </div>
        </div>

        <div className="w-full">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Durée Dev (mois)
          </label>
          <input
            type="number"
            value={state.projectDurationMonths}
            onChange={(e) => onInputChange('projectDurationMonths', parseInt(e.target.value))}
            min="1"
            className={`w-full px-4 py-3 rounded-lg border ${validationErrors.projectDurationMonths ? 'border-red-500' : 'border-slate-200'} focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none transition-all`}
          />
        </div>

        <div className="w-full">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Durée Vie (années)
          </label>
          <input
            type="number"
            value={state.projectLifespanYears}
            onChange={(e) => onInputChange('projectLifespanYears', parseInt(e.target.value))}
            min="1"
            className={`w-full px-4 py-3 rounded-lg border ${validationErrors.projectLifespanYears ? 'border-red-500' : 'border-slate-200'} focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none transition-all`}
          />
        </div>

        <div className="w-full">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Utilisateurs annuels
          </label>
          <input
            type="number"
            value={state.expectedUsers}
            onChange={(e) => onInputChange('expectedUsers', parseInt(e.target.value))}
            min="0"
            step="1000"
            className={`w-full px-4 py-3 rounded-lg border ${validationErrors.expectedUsers ? 'border-red-500' : 'border-slate-200'} focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none transition-all`}
          />
        </div>

        <div className="w-full">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Utilisation (h/sem)
          </label>
          <input
            type="number"
            value={state.avgUsageHoursPerWeek}
            onChange={(e) => onInputChange('avgUsageHoursPerWeek', parseFloat(e.target.value))}
            min="0"
            step="0.1"
            className={`w-full px-4 py-3 rounded-lg border ${validationErrors.avgUsageHoursPerWeek ? 'border-red-500' : 'border-slate-200'} focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none transition-all`}
          />
        </div>

        <div className="col-span-1 md:col-span-2">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Localisation Équipe
          </label>
          <div className="relative">
            <select
              value={state.devRegion}
              onChange={(e) => onInputChange('devRegion', e.target.value)}
              className={`w-full px-4 py-3 rounded-lg border ${validationErrors.devRegion ? 'border-red-500' : 'border-slate-200'} focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none transition-all appearance-none bg-white pr-10`}
            >
              <option value="">Sélectionnez une région</option>
              {regions.map(region => (
                <option key={region.name} value={region.name}>
                  {region.name} {region.countries && region.countries.length > 0 ? `(${region.countries?.slice(0, 2).join(", ")}${region.countries?.length > 2 ? ", etc." : ""})` : ""}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
              <ChevronDown className="w-5 h-5 text-slate-400" />
            </div>
          </div>
        </div>

        <div className="col-span-1 md:col-span-2">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Localisation Infrastructure
          </label>
          <div className="relative">
            <select
              value={state.infraRegion}
              onChange={(e) => onInputChange('infraRegion', e.target.value)}
              className={`w-full px-4 py-3 rounded-lg border ${validationErrors.infraRegion ? 'border-red-500' : 'border-slate-200'} focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none transition-all appearance-none bg-white pr-10`}
            >
              <option value="">Sélectionnez une région</option>
              {regions.map(region => (
                <option key={region.name} value={region.name}>
                  {region.name} {region.countries && region.countries.length > 0 ? `(${region.countries?.slice(0, 2).join(", ")}${region.countries?.length > 2 ? ", etc." : ""})` : ""}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
              <ChevronDown className="w-5 h-5 text-slate-400" />
            </div>
          </div>
        </div>

        <div className="col-span-1 md:col-span-2">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Localisation Utilisateurs
          </label>
          <div className="relative">
            <select
              value={state.userRegion}
              onChange={(e) => onInputChange('userRegion', e.target.value)}
              className={`w-full px-4 py-3 rounded-lg border ${validationErrors.userRegion ? 'border-red-500' : 'border-slate-200'} focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none transition-all appearance-none bg-white pr-10`}
            >
              <option value="">Sélectionnez une région</option>
              {regions.map(region => (
                <option key={region.name} value={region.name}>
                  {region.name} {region.countries && region.countries.length > 0 ? `(${region.countries?.slice(0, 2).join(", ")}${region.countries?.length > 2 ? ", etc." : ""})` : ""}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
              <ChevronDown className="w-5 h-5 text-slate-400" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarbonCalculatorForm; 